import { Badge } from '@/components/ui/catalyst/badge';

/**
 * Helper utilities for creating entity badges
 */

interface StatusBadgeConfig {
  readonly value: string;
  readonly colorMap?: Record<
    string,
    'lime' | 'amber' | 'zinc' | 'red' | 'blue' | 'sky' | 'emerald' | 'purple'
  >;
  readonly defaultColor?:
    | 'lime'
    | 'amber'
    | 'zinc'
    | 'red'
    | 'blue'
    | 'sky'
    | 'emerald'
    | 'purple';
}

/**
 * Create a status badge with optional color mapping
 */
export const createStatusBadge = ({
  value,
  colorMap = {},
  defaultColor = 'zinc',
}: StatusBadgeConfig) => {
  const color = colorMap[value] ?? defaultColor;
  return (
    <Badge
      color={color}
      className="h-5 min-w-0 px-2 text-[10px] font-medium tracking-wide"
    >
      {value}
    </Badge>
  );
};

/**
 * Create a count badge (e.g., "5 items")
 */
export const createCountBadge = (
  count: number,
  singularLabel: string,
  pluralLabel?: string,
  color:
    | 'lime'
    | 'amber'
    | 'zinc'
    | 'red'
    | 'blue'
    | 'sky'
    | 'emerald'
    | 'purple' = 'emerald',
) => {
  if (count === 0) return null;

  const label =
    count === 1 ? singularLabel : (pluralLabel ?? `${singularLabel}s`);
  return (
    <Badge
      color={color}
      className="h-5 min-w-0 px-2 text-[10px] font-medium tracking-wide"
    >
      {count} {label}
    </Badge>
  );
};

/**
 * Common enrichment status badge
 */
export const createEnrichmentBadge = (enrichmentStatus?: string | null) => {
  if (!enrichmentStatus) return null;

  const config: Record<
    string,
    { color: 'lime' | 'sky' | 'zinc'; label: string }
  > = {
    enrichment_succeeded: { color: 'lime', label: 'Enriched' },
    enrichment_in_progress: { color: 'sky', label: 'Enriching...' },
    enrichment_failed: { color: 'zinc', label: 'Not enriched' },
  };

  const badgeConfig = config[enrichmentStatus] ?? {
    color: 'zinc',
    label: 'Not enriched',
  };

  return (
    <Badge
      color={badgeConfig.color}
      className="h-5 min-w-0 px-2 text-[10px] font-medium tracking-wide"
    >
      {badgeConfig.label}
    </Badge>
  );
};
